import { Box } from "@mui/material";
import { ScoutHost } from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { ScoutSubmitHandler } from "./ScoutInput";

import { useScoutAllowMutations } from "../hooks/useScoutAllowMutations";
import { useScoutConversation } from "../hooks/useScoutConversation";
import { SCOUT_FULL_PAGE_HEADER_HEIGHT_PX, SCOUT_SCROLLBAR_GUTTER_PX } from "../scoutLayout";
import { mapScoutAttachmentToPreview } from "../scoutSubmitAttachments";
import { ChatChrome } from "./ChatChrome";
import { NewChat } from "./NewChat";
import { ScoutHostProvider } from "./ScoutHostContext";
import { LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND } from "./scoutLargeFileFullAnalysisProposal";
import { ScoutProposalProvider } from "./ScoutProposalContext";
import { ScoutProUpsellProvider } from "./ScoutProUpsellContext";
import { TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND } from "./scoutTenantDocumentProposal";

export type ScoutConversationSettingsState = {
  allowMutations: boolean;
  onAllowMutationsChange: (next: boolean) => void;
  settingsDisabled: boolean;
};

export const ScoutChat = ({
  onHasMessagesChange,
  onConversationTitleChange,
  onConversationSettingsChange,
  hasScoutProAccess = false,
}: {
  onHasMessagesChange?: (hasMessages: boolean) => void;
  onConversationTitleChange?: (input: {
    conversationId: string | undefined;
    title: string | undefined;
  }) => void;
  onConversationSettingsChange?: (settings: ScoutConversationSettingsState) => void;
  hasScoutProAccess?: boolean;
}) => {
  const navigate = useNavigate();
  const { tenantId, conversationId } = useParams();
  const handleConversationIdChange = useCallback(
    (nextConversationId: string) => {
      if (!tenantId) {
        return;
      }

      navigate(`/o/${tenantId}/scout/${nextConversationId}`, { replace: true });
    },
    [navigate, tenantId]
  );
  const {
    conversation,
    messages,
    activeTurn,
    streamStatus,
    loading,
    isSending,
    isAborting,
    isLoadingOlderMessages,
    hasOlderMessages,
    sendMessage,
    abortTurn,
    loadOlderMessages,
    privateDocuments,
    largeDocumentOptIns,
    dismissLargeDocumentOptIn,
    confirmLargeDocumentAnalysis,
    isConfirmingLargeDocumentAnalysis,
    isDecliningLargeDocumentAnalysis,
    registerComposerAttachments,
    unregisterComposerAttachment,
    respondedTenantDocumentProposals,
    isProcessingTenantDocumentProposal,
    confirmTenantDocumentProposal,
    declineTenantDocumentProposal,
    respondedLargeFileFullAnalysisProposals,
    confirmLargeFileFullAnalysisProposal,
    declineLargeFileFullAnalysisProposal,
    dismissedScoutProUpsells,
    dismissScoutProUpsell,
  } = useScoutConversation({
    tenantId,
    conversationId,
    onConversationIdChange: handleConversationIdChange,
    enablePrivateDocuments: hasScoutProAccess,
  });
  const proposalContextValue = useMemo(
    () => ({
      handlers: {
        [TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND]: {
          respondedProposals: respondedTenantDocumentProposals,
          isProcessing: isProcessingTenantDocumentProposal,
          onConfirm: confirmTenantDocumentProposal,
          onDecline: declineTenantDocumentProposal,
        },
        [LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND]: {
          respondedProposals: respondedLargeFileFullAnalysisProposals,
          isProcessing: isConfirmingLargeDocumentAnalysis || isDecliningLargeDocumentAnalysis,
          onConfirm: confirmLargeFileFullAnalysisProposal,
          onDecline: declineLargeFileFullAnalysisProposal,
        },
      },
    }),
    [
      confirmLargeFileFullAnalysisProposal,
      confirmTenantDocumentProposal,
      declineLargeFileFullAnalysisProposal,
      declineTenantDocumentProposal,
      isConfirmingLargeDocumentAnalysis,
      isDecliningLargeDocumentAnalysis,
      isProcessingTenantDocumentProposal,
      respondedLargeFileFullAnalysisProposals,
      respondedTenantDocumentProposals,
    ]
  );
  const scoutProUpsellContextValue = useMemo(
    () => ({
      dismissedUpsells: dismissedScoutProUpsells,
      onDismiss: dismissScoutProUpsell,
    }),
    [dismissedScoutProUpsells, dismissScoutProUpsell]
  );
  const hasMessages = messages.length > 0;
  const isConversationRoute = Boolean(conversationId);
  const isLoadingConversation = isConversationRoute && loading && !hasMessages;
  useEffect(() => {
    onHasMessagesChange?.(hasMessages);
  }, [hasMessages, onHasMessagesChange]);
  useEffect(() => {
    onConversationTitleChange?.({
      conversationId: conversation?.id,
      title: conversation?.title,
    });
  }, [conversation?.id, conversation?.title, onConversationTitleChange]);
  const isTurnRunning =
    isSending ||
    isAborting ||
    activeTurn?.status === "starting" ||
    activeTurn?.status === "streaming";

  const { allowMutations, onAllowMutationsChange } = useScoutAllowMutations({
    conversationId,
    conversationAllowMutations: conversation?.allowMutations,
  });

  useEffect(() => {
    onConversationSettingsChange?.({
      allowMutations: hasScoutProAccess ? allowMutations : false,
      onAllowMutationsChange: hasScoutProAccess ? onAllowMutationsChange : () => undefined,
      settingsDisabled: isTurnRunning || !hasScoutProAccess,
    });
  }, [
    allowMutations,
    hasScoutProAccess,
    isTurnRunning,
    onAllowMutationsChange,
    onConversationSettingsChange,
  ]);

  const handleSubmit = useCallback<ScoutSubmitHandler>(
    async (message, options) => {
      const attachments = options?.attachments;
      await sendMessage(message, {
        allowMutations: hasScoutProAccess ? allowMutations : false,
        host: ScoutHost.FullPage,
        attachmentIds: hasScoutProAccess
          ? attachments?.map((attachment) => attachment.privateDocumentId)
          : undefined,
        attachmentPreviews: hasScoutProAccess
          ? attachments?.map(mapScoutAttachmentToPreview)
          : undefined,
        extractionWatchPrivateDocumentIds: options?.extractionWatchPrivateDocumentIds,
      });
    },
    [allowMutations, hasScoutProAccess, sendMessage]
  );

  const scoutHostContextValue = useMemo(() => ({ host: ScoutHost.FullPage }), []);

  return (
    <ScoutHostProvider value={scoutHostContextValue}>
      <ScoutProposalProvider value={proposalContextValue}>
        <ScoutProUpsellProvider value={scoutProUpsellContextValue}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              alignItems: "center",
            }}
          >
            {!hasMessages && !isConversationRoute ? (
              <NewChat
                tenantId={tenantId}
                onSubmit={handleSubmit}
                onAbort={abortTurn}
                isSending={isTurnRunning}
                isAborting={isAborting}
                onAttachmentsUploaded={registerComposerAttachments}
                onAttachmentRemoved={unregisterComposerAttachment}
                allowMutations={hasScoutProAccess ? allowMutations : false}
                onAllowMutationsChange={hasScoutProAccess ? onAllowMutationsChange : undefined}
                hasScoutProAccess={hasScoutProAccess}
              />
            ) : (
              <ChatChrome
                tenantId={tenantId}
                conversationId={conversationId}
                messages={messages}
                activeTurn={activeTurn}
                streamStatus={streamStatus}
                loading={isLoadingConversation}
                initialPinned={isConversationRoute}
                onSubmit={handleSubmit}
                onAbort={abortTurn}
                isSending={isTurnRunning}
                isAborting={isAborting}
                onLoadOlderMessages={loadOlderMessages}
                isLoadingOlderMessages={isLoadingOlderMessages}
                hasOlderMessages={hasOlderMessages}
                privateDocuments={privateDocuments}
                largeDocumentOptIns={largeDocumentOptIns}
                onDismissLargeDocumentOptIn={dismissLargeDocumentOptIn}
                onConfirmLargeDocumentAnalysis={confirmLargeDocumentAnalysis}
                isConfirmingLargeDocumentAnalysis={isConfirmingLargeDocumentAnalysis}
                isDecliningLargeDocumentAnalysis={isDecliningLargeDocumentAnalysis}
                onAttachmentsUploaded={registerComposerAttachments}
                onAttachmentRemoved={unregisterComposerAttachment}
                allowMutations={hasScoutProAccess ? allowMutations : false}
                onAllowMutationsChange={hasScoutProAccess ? onAllowMutationsChange : undefined}
                hasScoutProAccess={hasScoutProAccess}
                headerOverlayPx={SCOUT_FULL_PAGE_HEADER_HEIGHT_PX}
                footerScrollbarGutterPx={SCOUT_SCROLLBAR_GUTTER_PX}
              />
            )}
          </Box>
        </ScoutProUpsellProvider>
      </ScoutProposalProvider>
    </ScoutHostProvider>
  );
};
